import type { StandardDispenseUnitTypeT } from '@lotushealth/lotushealth_js_sdk/schemas';

export interface DispenseUnitOption {
  value: string;
  label: string;
  group: 'Common units' | 'All other units';
}

const COMMON_UNIT_ORDER = [
  'tablet',
  'capsule',
  'milliliter',
  'gram',
  'each',
  'unit',
  'vial',
  'syringe',
  'inhaler',
  'patch',
  'package',
  'tube',
] as const;

const commonUnitRank = new Map<string, number>(
  COMMON_UNIT_ORDER.map((name, index) => [name, index])
);
const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

function unitName(unit: StandardDispenseUnitTypeT): string {
  return unit.Name?.trim() || unit.SingularOrPlural?.trim() || 'Unit';
}

function unitLabel(unit: StandardDispenseUnitTypeT): string {
  const name = unitName(unit);
  const abbreviation = unit.Abbreviation?.trim();
  return abbreviation ? `${name} (${abbreviation})` : name;
}

/**
 * Keep the complete active DoseSpot list, but make it navigable: familiar
 * prescribing units appear first and everything else is alphabetical.
 */
export function buildDispenseUnitOptions(
  units: StandardDispenseUnitTypeT[] | null | undefined
): DispenseUnitOption[] {
  const unique = new Map<number, StandardDispenseUnitTypeT>();
  for (const unit of units ?? []) {
    const id = unit.StandardDispenseUnitTypeID;
    if (id == null || unit.IsActive === false || unique.has(id)) {
      continue;
    }
    unique.set(id, unit);
  }

  const common: StandardDispenseUnitTypeT[] = [];
  const other: StandardDispenseUnitTypeT[] = [];
  for (const unit of unique.values()) {
    if (commonUnitRank.has(unitName(unit).toLowerCase())) {
      common.push(unit);
    } else {
      other.push(unit);
    }
  }

  common.sort((a, b) => {
    const aRank = commonUnitRank.get(unitName(a).toLowerCase()) ?? Number.MAX_SAFE_INTEGER;
    const bRank = commonUnitRank.get(unitName(b).toLowerCase()) ?? Number.MAX_SAFE_INTEGER;
    return aRank - bRank;
  });
  other.sort((a, b) => collator.compare(unitLabel(a), unitLabel(b)));

  return [
    ...common.map((unit) => ({
      value: String(unit.StandardDispenseUnitTypeID),
      label: unitLabel(unit),
      group: 'Common units' as const,
    })),
    ...other.map((unit) => ({
      value: String(unit.StandardDispenseUnitTypeID),
      label: unitLabel(unit),
      group: 'All other units' as const,
    })),
  ];
}
